import HttpService from './HttpService';

const formatError = (error, defaultMessage) => {
  const errorMessage = error.response?.data?.message || defaultMessage;
  const errorCode = error.response?.status;
  const details = error.response?.data?.details || {};

  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.error(defaultMessage, error);
  }

  return { message: errorMessage, code: errorCode, originalError: error, details };
};

const UtilityService = {
  /**
   * List utilities for a house, with computed monthly average and bill count.
   * @param {number} houseId
   * @returns {Promise<{utilities: Array}>}
   */
  getUtilities: async (houseId) => {
    try {
      const response = await HttpService.get(`/houses/${houseId}/utilities`);
      return response.data;
    } catch (error) {
      throw formatError(error, 'Error fetching utilities');
    }
  },

  /**
   * Get one utility by id.
   */
  getUtility: async (id) => {
    try {
      const response = await HttpService.get(`/utilities/${id}`);
      return response.data;
    } catch (error) {
      throw formatError(error, `Error fetching utility ${id}`);
    }
  },

  /**
   * Create a utility under a house.
   * @param {number} houseId
   * @param {{type:string, name?:string, account_url?:string, notes?:string}} data
   */
  createUtility: async (houseId, data) => {
    try {
      const response = await HttpService.post(`/houses/${houseId}/utilities`, data);
      return response.data;
    } catch (error) {
      throw formatError(error, 'Error creating utility');
    }
  },

  /**
   * Update a utility's metadata.
   */
  updateUtility: async (id, data) => {
    try {
      const response = await HttpService.put(`/utilities/${id}`, data);
      return response.data;
    } catch (error) {
      throw formatError(error, `Error updating utility ${id}`);
    }
  },

  /**
   * Delete a utility (cascades to bills + R2 files).
   */
  deleteUtility: async (id) => {
    try {
      const response = await HttpService.delete(`/utilities/${id}`);
      return response.data;
    } catch (error) {
      throw formatError(error, `Error deleting utility ${id}`);
    }
  },

  /**
   * List bills for a utility.
   */
  getBills: async (utilityId) => {
    try {
      const response = await HttpService.get(`/utilities/${utilityId}/bills`);
      return response.data;
    } catch (error) {
      throw formatError(error, 'Error fetching bills');
    }
  },

  /**
   * Create a bill, optionally with an attached file (PDF or image).
   * @param {number} utilityId
   * @param {{period_start:string, period_end:string, statement_date?:string,
   *   amount:number|string, usage_quantity?:number|string, usage_unit?:string,
   *   notes?:string, file?:File}} data
   */
  createBill: async (utilityId, data) => {
    try {
      const formData = new FormData();
      formData.append('period_start', data.period_start);
      formData.append('period_end', data.period_end);
      formData.append('amount', String(data.amount));
      if (data.statement_date) formData.append('statement_date', data.statement_date);
      if (data.usage_quantity != null && data.usage_quantity !== '') {
        formData.append('usage_quantity', String(data.usage_quantity));
      }
      if (data.usage_unit) formData.append('usage_unit', data.usage_unit);
      if (data.notes != null) formData.append('notes', data.notes);
      if (data.file) formData.append('file', data.file);

      const response = await HttpService.post(
        `/utilities/${utilityId}/bills`,
        formData,
      );
      return response.data;
    } catch (error) {
      throw formatError(error, 'Error creating bill');
    }
  },

  /**
   * Update a bill's metadata.
   */
  updateBill: async (id, data) => {
    try {
      const response = await HttpService.put(`/bills/${id}`, data);
      return response.data;
    } catch (error) {
      throw formatError(error, `Error updating bill ${id}`);
    }
  },

  /**
   * Attach a file (PDF or image) to an existing bill, replacing any current one.
   * Returns the refreshed bill (fresh presigned url).
   * @param {number} id bill id
   * @param {File} file
   */
  attachBillFile: async (id, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await HttpService.post(`/bills/${id}/file`, formData);
      return response.data;
    } catch (error) {
      throw formatError(error, `Error uploading bill file ${id}`);
    }
  },

  /**
   * Extract bill fields from an uploaded PDF/image for review (nothing is
   * persisted; counts as one scan). Returns {success, bill}.
   * @param {number} utilityId
   * @param {File} file
   */
  extractBill: async (utilityId, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await HttpService.post(
        `/utilities/${utilityId}/bills/extract`,
        formData,
      );
      return response.data;
    } catch (error) {
      throw formatError(error, 'Error reading bill file');
    }
  },

  /**
   * Import one bill file in a single fused server-side pass: parse -> validate
   * -> dedupe -> upload -> insert. Unlike extractBill (which returns fields for
   * the user to review), a clean bill is CREATED by this call.
   *
   * The bulk-import UI calls this once per file. One request per file is
   * deliberate (ADR-0010): it keeps every request far under the server's
   * request timeout with no chunking, at the cost of one round trip each.
   *
   * Resolves for every per-file outcome — read `outcome`, not the HTTP status.
   * Only request-level failures (404 / 503 / quota) reject.
   *
   * @param {number} utilityId
   * @param {File} file
   * @param {{clientId?: string}} [opts]
   * @returns {Promise<{outcome: 'created'|'needs_attention'|'skipped_duplicate'|'failed',
   *   client_id: ?string, original_filename: ?string, parse_path: ?string,
   *   bill: ?Object, fields: ?Object, confidence: ?number, signals: string[],
   *   error: ?string, error_code: ?string, missing: ?string[], errors: ?Array,
   *   existing_bill_id: ?number}>}
   */
  importBill: async (utilityId, file, opts = {}) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (opts.clientId) formData.append('client_id', opts.clientId);
      const response = await HttpService.post(
        `/utilities/${utilityId}/bills/import`,
        formData,
      );
      return response.data;
    } catch (error) {
      throw formatError(error, 'Error importing bill');
    }
  },

  /**
   * Delete a bill (and its R2 file).
   */
  deleteBill: async (id) => {
    try {
      const response = await HttpService.delete(`/bills/${id}`);
      return response.data;
    } catch (error) {
      throw formatError(error, `Error deleting bill ${id}`);
    }
  },
};

export default UtilityService;
